// ═══════════════════════════════════════════════════════════════════════════════
// Settlement list + helper create flow
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { parseDate, RECEIPT_STATUS, USER_ROLE } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { budgetApi, receiptApi, settlementApi } from '../../lib/api';
import {
  MODAL_BACKDROP_BOTTOM_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
} from '../../lib/modalClasses';
import { formatIsoDateForDailyBreakdownTable, formatSettlementPeriodDisplay } from '../../lib/settlementDisplayFormat';
import { EmployerMoneyGivenSettlementSection } from '../../components/settlement/EmployerMoneyGivenSettlementSection';
import { MoneyGivenVsExpensesChart } from '../../components/settlement/employer/MoneyGivenVsExpensesChart';
import { HelperMoneyReceivedPeriodSelect } from '../../components/settlement/HelperMoneyReceivedPeriodSelect';
import { MoneyReceivedDetailModal } from '../../components/settlement/MoneyReceivedDetailModal';
import { SettlementHelpPopover } from '../../components/settlement/SettlementHelpPopover';
import { ModalCircleCloseButton } from '../../components/ui/ModalCircleCloseButton';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { MoneyGivenCashWatermark } from '../../components/home/MoneyGivenCashWatermark';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import {
  buildHelperReceivedVsExpensesBars,
  settlementBelongsToBudget,
} from '../../utils/employerMoneyGivenSettlement';

const HELPER_MAIN_BUDGET_DROPDOWN_LIMIT = 10;
import { getTodayIsoDateInTimezone } from '../../components/receipt/ReceiptListHistoryStyleGrouped';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { getCurrentWeekRangeByTimezone } from '../../lib/currentWeekRange';
import type { BudgetListItem, ReceiptListItem, SettlementListItem, SettlementSummary } from '@fdh/shared';

type PreviewState = SettlementSummary | null;
type HelperSubmittedPreviewState = {
  id: string;
  startDate: string;
  endDate: string;
  budgetAmount: number;
  totalSpent: number;
  balance: number;
} | null;

const isIsoDate = (value: string | null): value is string =>
  value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value);

const defaultHelperDateRange = (timezone: string): { start: string; end: string } => {
  const weekRange = getCurrentWeekRangeByTimezone(timezone);
  return {
    start: weekRange.startDate,
    end: weekRange.endDate,
  };
};

/** Helper expenses hub — matches MoneyGivenVsExpensesChart card border and shadow */
const HELPER_EXPENSES_SOFT_CARD_CLASS =
  'rounded-2xl border border-gray-300/80 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900';

const HelperStatInfoIcon = () => (
  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const HELPER_QUICK_ACTION_CARD_CLASS =
  'flex min-h-[100px] flex-col rounded-xl border border-gray-200/60 bg-white p-3 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700/60 dark:bg-gray-900 dark:shadow-black/20';

const toDateOnly = (value: string): Date | null => {
  const parsed = parseDate(value);
  if (!parsed) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const countOverlapDaysInclusive = (
  rangeStart: string,
  rangeEnd: string,
  periodStart: string,
  periodEnd: string
): number => {
  const aStart = toDateOnly(rangeStart);
  const aEnd = toDateOnly(rangeEnd);
  const bStart = toDateOnly(periodStart);
  const bEnd = toDateOnly(periodEnd);
  if (!aStart || !aEnd || !bStart || !bEnd) {
    return 0;
  }
  const overlapStart = Math.max(aStart.getTime(), bStart.getTime());
  const overlapEnd = Math.min(aEnd.getTime(), bEnd.getTime());
  if (overlapEnd < overlapStart) {
    return 0;
  }
  const dayMs = 24 * 60 * 60 * 1000;
  return Math.floor((overlapEnd - overlapStart) / dayMs) + 1;
};

const countDaysInclusive = (start: string, end: string): number => {
  const s = toDateOnly(start);
  const e = toDateOnly(end);
  if (!s || !e || e.getTime() < s.getTime()) {
    return 0;
  }
  const dayMs = 24 * 60 * 60 * 1000;
  return Math.floor((e.getTime() - s.getTime()) / dayMs) + 1;
};

const sortSettlementsOldestToNewest = (rows: SettlementListItem[]): SettlementListItem[] => {
  return [...rows].sort((a, b) => {
    if (a.startDate !== b.startDate) {
      return a.startDate.localeCompare(b.startDate);
    }
    if (a.endDate !== b.endDate) {
      return a.endDate.localeCompare(b.endDate);
    }
    return a.id.localeCompare(b.id);
  });
};

const formatSettlementRangesText = (rows: SettlementListItem[]): string =>
  rows.map((row) => `${row.startDate} to ${row.endDate}`).join(', ');

const resolveHelperPeriodSettlementNotice = (
  mainBudget: BudgetListItem,
  overlappingRecords: SettlementListItem[],
  t: (key: string, params?: Record<string, string>) => string
): string => {
  if (overlappingRecords.every((row) => row.status === 'reviewed')) {
    return t('settlement.helperReviewedByEmployerNotice');
  }
  const budgetSettlement =
    overlappingRecords.find((row) => settlementBelongsToBudget(row, mainBudget)) ??
    overlappingRecords[0] ??
    null;
  if (budgetSettlement?.status === 'reviewed') {
    return t('settlement.helperReviewedByEmployerNotice');
  }
  const rangesText = formatSettlementRangesText(overlappingRecords);
  const submittedOnly = overlappingRecords.filter((row) => row.status === 'submitted');
  if (submittedOnly.length === overlappingRecords.length) {
    if (overlappingRecords.length === 1) {
      return t('settlement.helperSubmittedNotice');
    }
    return t('settlement.helperSubmittedRangesNotice', { ranges: rangesText });
  }
  return t('settlement.helperSubmittedAndReviewedRangesNotice', { ranges: rangesText });
};

export const SettlementListPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const employerFinanceTimezone = resolveDisplayTimeZone(user?.settings.timezone);

  const [employerTitleMenuOpen, setEmployerTitleMenuOpen] = useState(false);
  const employerTitleMenuRef = useRef<HTMLDivElement>(null);

  const [list, setList] = useState<SettlementListItem[]>([]);
  const [preview, setPreview] = useState<PreviewState>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [helperSubmittedNotice, setHelperSubmittedNotice] = useState<string | null>(null);
  const [helperSubmittedRecordPreview, setHelperSubmittedRecordPreview] =
    useState<HelperSubmittedPreviewState>(null);
  const [helperMainBudgets, setHelperMainBudgets] = useState<BudgetListItem[]>([]);
  const [helperMainBudgetsLoading, setHelperMainBudgetsLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [dailyDetailModalOpen, setDailyDetailModalOpen] = useState(false);
  const [moneyReceivedDetailModalOpen, setMoneyReceivedDetailModalOpen] = useState(false);

  const isHelper = user?.role === USER_ROLE.HELPER;
  const isEmployer = user?.role === USER_ROLE.EMPLOYER;

  const helperSelectedMainBudgetId = useMemo(() => {
    if (!isHelper) {
      return '';
    }
    return searchParams.get('mainBudgetId') ?? '';
  }, [isHelper, searchParams]);

  const helperSelectedMainBudget = useMemo(
    () => helperMainBudgets.find((row) => row.id === helperSelectedMainBudgetId) ?? null,
    [helperMainBudgets, helperSelectedMainBudgetId]
  );

  const helperSettlementStart = useMemo(() => {
    if (!isHelper) {
      return '';
    }
    if (helperSelectedMainBudget) {
      return helperSelectedMainBudget.startDate;
    }
    return defaultHelperDateRange(employerFinanceTimezone).start;
  }, [employerFinanceTimezone, helperSelectedMainBudget, isHelper]);

  const helperSettlementEnd = useMemo(() => {
    if (!isHelper) {
      return '';
    }
    if (helperSelectedMainBudget) {
      return helperSelectedMainBudget.endDate;
    }
    return defaultHelperDateRange(employerFinanceTimezone).end;
  }, [employerFinanceTimezone, helperSelectedMainBudget, isHelper]);

  const commitHelperMainBudgetIdToUrl = useCallback(
    (mainBudgetId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('mainBudgetId', mainBudgetId);
      next.delete('helperStart');
      next.delete('helperEnd');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const helperConfirmSearch = useMemo(() => {
    if (!isHelper) {
      return '';
    }
    const p = new URLSearchParams();
    if (helperSelectedMainBudgetId !== '') {
      p.set('mainBudgetId', helperSelectedMainBudgetId);
    }
    return p.toString();
  }, [helperSelectedMainBudgetId, isHelper]);

  const loadList = useCallback(async () => {
    setListError(null);
    try {
      const pageSize = 100;
      let page = 1;
      let merged: SettlementListItem[] = [];
      while (page <= 20) {
        const res = await settlementApi.list('all', page, pageSize);
        if (!res.success || !res.data) {
          if (page === 1) {
            setListError(res.error ?? t('settlement.error'));
          }
          break;
        }
        const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        merged = [...merged, ...items];
        if (items.length < pageSize) {
          break;
        }
        page += 1;
      }
      setList(merged);
    } catch {
      setListError(t('settlement.error'));
    }
  }, [t]);

  useEffect(() => {
    if (!isHelper) {
      return;
    }
    const loadHelperMainBudgets = async () => {
      setHelperMainBudgetsLoading(true);
      try {
        const res = await budgetApi.list(
          'all',
          1,
          HELPER_MAIN_BUDGET_DROPDOWN_LIMIT,
          '2000-01-01',
          '9999-12-31'
        );
        if (!res.success || !res.data) {
          setHelperMainBudgets([]);
          return;
        }
        const rows = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        const sortedRows = [...rows].sort((a, b) => {
          if (a.startDate !== b.startDate) {
            return b.startDate.localeCompare(a.startDate);
          }
          return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
        });
        setHelperMainBudgets(sortedRows.slice(0, HELPER_MAIN_BUDGET_DROPDOWN_LIMIT));
      } finally {
        setHelperMainBudgetsLoading(false);
      }
    };
    void loadHelperMainBudgets();
  }, [isHelper]);

  useEffect(() => {
    if (!isHelper) {
      return;
    }
    if (helperMainBudgets.length === 0) {
      return;
    }
    const hasSelectedMainBudget = helperMainBudgets.some((row) => row.id === helperSelectedMainBudgetId);
    if (hasSelectedMainBudget) {
      return;
    }
    const todayYmd = getTodayIsoDateInTimezone(employerFinanceTimezone);
    const todaysBudget = helperMainBudgets.find(
      (row) => row.startDate <= todayYmd && row.endDate >= todayYmd
    );
    const todayDate = toDateOnly(todayYmd);
    const fallbackBudget = todayDate
      ? helperMainBudgets.reduce<BudgetListItem | null>((closest, row) => {
          const rowStart = toDateOnly(row.startDate);
          if (!rowStart) {
            return closest;
          }
          if (!closest) {
            return row;
          }
          const closestStart = toDateOnly(closest.startDate);
          if (!closestStart) {
            return row;
          }
          const rowDistance = Math.abs(rowStart.getTime() - todayDate.getTime());
          const closestDistance = Math.abs(closestStart.getTime() - todayDate.getTime());
          if (rowDistance < closestDistance) {
            return row;
          }
          if (rowDistance > closestDistance) {
            return closest;
          }
          return row.startDate > closest.startDate ? row : closest;
        }, null)
      : null;
    const nextBudget = todaysBudget ?? fallbackBudget;
    if (!nextBudget) {
      return;
    }
    commitHelperMainBudgetIdToUrl(nextBudget.id);
  }, [
    commitHelperMainBudgetIdToUrl,
    employerFinanceTimezone,
    helperMainBudgets,
    helperSelectedMainBudgetId,
    isHelper,
  ]);

  useEffect(() => {
    if (!isHelper) {
      return;
    }
    void loadList();
  }, [isHelper, loadList]);

  useEffect(() => {
    if (!dailyDetailModalOpen && !moneyReceivedDetailModalOpen) {
      return undefined;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      e.preventDefault();
      if (moneyReceivedDetailModalOpen) {
        setMoneyReceivedDetailModalOpen(false);
        return;
      }
      setDailyDetailModalOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [dailyDetailModalOpen, moneyReceivedDetailModalOpen]);

  const buildHelperRangeSummary = useCallback(async () => {
    const pageSize = 100;
    const fetchReceiptRows = async (
      page: number,
      acc: ReceiptListItem[]
    ): Promise<ReceiptListItem[]> => {
      if (page > 20) {
        return acc;
      }
      const receiptRes = await receiptApi.list({
        status: 'all',
        startDate: helperSettlementStart,
        endDate: helperSettlementEnd,
        page,
        pageSize,
      });
      if (!receiptRes.success || !receiptRes.data) {
        return acc;
      }
      const rows = Array.isArray(receiptRes.data) ? receiptRes.data : (receiptRes.data.items ?? []);
      const merged = [...acc, ...rows];
      if (rows.length < pageSize) {
        return merged;
      }
      return fetchReceiptRows(page + 1, merged);
    };
    const fetchBudgetRows = async (
      page: number,
      acc: BudgetListItem[]
    ): Promise<BudgetListItem[]> => {
      if (page > 20) {
        return acc;
      }
      const budgetRes = await budgetApi.list('all', page, pageSize, helperSettlementStart, helperSettlementEnd);
      if (!budgetRes.success || !budgetRes.data) {
        return acc;
      }
      const rows = Array.isArray(budgetRes.data) ? budgetRes.data : (budgetRes.data.items ?? []);
      const merged = [...acc, ...rows];
      if (rows.length < pageSize) {
        return merged;
      }
      return fetchBudgetRows(page + 1, merged);
    };
    const [receiptRows, budgetRows] = await Promise.all([
      fetchReceiptRows(1, []),
      fetchBudgetRows(1, []),
    ]);
    const totalSpent = receiptRows
      .filter(
        (row) =>
          row.status === RECEIPT_STATUS.SAVE ||
          row.status === RECEIPT_STATUS.SUBMITTED ||
          row.status === RECEIPT_STATUS.REVIEWED
      )
      .reduce((sum, row) => sum + row.totalAmount, 0);
    const budgetAmount = budgetRows.reduce((sum, row) => {
      const overlapDays = countOverlapDaysInclusive(
        helperSettlementStart,
        helperSettlementEnd,
        row.startDate,
        row.endDate
      );
      if (overlapDays === 0) {
        return sum;
      }
      const totalDays = countDaysInclusive(row.startDate, row.endDate);
      if (totalDays === 0) {
        return sum;
      }
      return sum + (row.amount * overlapDays) / totalDays;
    }, 0);
    return {
      budgetAmount,
      totalSpent,
      balance: budgetAmount - totalSpent,
    };
  }, [helperSettlementEnd, helperSettlementStart]);

  const runHelperPreview = useCallback(async () => {
    if (!helperSelectedMainBudget || !helperSettlementStart || !helperSettlementEnd || helperSettlementStart > helperSettlementEnd) {
      return;
    }
    const overlappingSubmittedOrReviewedRecords = sortSettlementsOldestToNewest(
      list.filter(
      (row) =>
        (row.status === 'submitted' || row.status === 'reviewed') &&
        row.startDate <= helperSettlementEnd &&
        row.endDate >= helperSettlementStart
    ));
    if (overlappingSubmittedOrReviewedRecords.length > 0) {
      setPreviewLoading(true);
      setFormError(null);
      setPreview(null);
      try {
        const summary = await buildHelperRangeSummary();
        const primary = overlappingSubmittedOrReviewedRecords[0];
        setHelperSubmittedRecordPreview({
          id: primary.id,
          startDate: primary.startDate,
          endDate: primary.endDate,
          budgetAmount: summary.budgetAmount,
          totalSpent: summary.totalSpent,
          balance: summary.balance,
        });
        setHelperSubmittedNotice(
          resolveHelperPeriodSettlementNotice(
            helperSelectedMainBudget,
            overlappingSubmittedOrReviewedRecords,
            t
          )
        );
        return;
      } finally {
        setPreviewLoading(false);
      }
    }
    setPreviewLoading(true);
    setFormError(null);
    setHelperSubmittedNotice(null);
    setHelperSubmittedRecordPreview(null);
    try {
      const res = await settlementApi.preview({
        mainBudgetId: helperSelectedMainBudget.id,
      });
      if (res.success && res.data) {
        setPreview(res.data);
      } else {
        const fallbackMessage = res.error ?? t('settlement.error');
        if (
          fallbackMessage.includes('pending employer decision') ||
          fallbackMessage.includes('already settled and locked')
        ) {
          const submittedAndReviewedRecords = sortSettlementsOldestToNewest(
            list.filter(
              (row) =>
                (row.status === 'submitted' || row.status === 'reviewed') &&
                row.startDate <= helperSettlementEnd &&
                row.endDate >= helperSettlementStart
            )
          );
          if (submittedAndReviewedRecords.length > 0) {
            const summary = await buildHelperRangeSummary();
            const firstSubmittedRecord = submittedAndReviewedRecords[0];
            setHelperSubmittedRecordPreview({
              id: firstSubmittedRecord.id,
              startDate: firstSubmittedRecord.startDate,
              endDate: firstSubmittedRecord.endDate,
              budgetAmount: summary.budgetAmount,
              totalSpent: summary.totalSpent,
              balance: summary.balance,
            });
          }
          setHelperSubmittedNotice(
            submittedAndReviewedRecords.length > 0
              ? resolveHelperPeriodSettlementNotice(
                  helperSelectedMainBudget,
                  submittedAndReviewedRecords,
                  t
                )
              : t('settlement.helperSubmittedNotice')
          );
          return;
        }
        setFormError(fallbackMessage);
      }
    } catch (err) {
      const errorMessage = resolveApiErrorMessage(err, t, 'settlement.error');
      if (
        errorMessage.includes('pending employer decision') ||
        errorMessage.includes('already settled and locked')
      ) {
        const submittedAndReviewedRecords = sortSettlementsOldestToNewest(
          list.filter(
            (row) =>
              (row.status === 'submitted' || row.status === 'reviewed') &&
              row.startDate <= helperSettlementEnd &&
              row.endDate >= helperSettlementStart
          )
        );
        if (submittedAndReviewedRecords.length > 0) {
          const summary = await buildHelperRangeSummary();
          const firstSubmittedRecord = submittedAndReviewedRecords[0];
          setHelperSubmittedRecordPreview({
            id: firstSubmittedRecord.id,
            startDate: firstSubmittedRecord.startDate,
            endDate: firstSubmittedRecord.endDate,
            budgetAmount: summary.budgetAmount,
            totalSpent: summary.totalSpent,
            balance: summary.balance,
          });
        }
        setHelperSubmittedNotice(
          submittedAndReviewedRecords.length > 0
            ? resolveHelperPeriodSettlementNotice(
                helperSelectedMainBudget,
                submittedAndReviewedRecords,
                t
              )
            : t('settlement.helperSubmittedNotice')
        );
        return;
      }
      setFormError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  }, [buildHelperRangeSummary, helperSelectedMainBudget, helperSettlementEnd, helperSettlementStart, list, t]);

  useEffect(() => {
    if (!isHelper) {
      return undefined;
    }
    if (helperMainBudgets.length === 0) {
      setPreview(null);
      setFormError(null);
      return undefined;
    }
    if (!helperSettlementStart || !helperSettlementEnd || helperSettlementStart > helperSettlementEnd) {
      setPreview(null);
      if (
        helperSettlementStart !== '' &&
        helperSettlementEnd !== '' &&
        helperSettlementStart > helperSettlementEnd
      ) {
        setFormError(t('budget.validation.dates'));
      } else {
        setFormError(null);
      }
      return undefined;
    }
    setFormError(null);
    const timerId = window.setTimeout(() => {
      void runHelperPreview();
    }, 400);
    return () => window.clearTimeout(timerId);
  }, [helperMainBudgets.length, helperSettlementEnd, helperSettlementStart, isHelper, runHelperPreview, t]);

  const formatMoney = (n: number) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);
  const formatMoneyCompact = (n: number) => `$${Number(n.toFixed(2)).toString()}`;

  const balanceLabel = (balance: number) => {
    if (balance >= 0) return t('settlement.surplus');
    return t('settlement.overspent');
  };

  const balanceClass = (balance: number) =>
    balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  const helperPreviewBudgetAmount = preview?.settlement.budgetAmount ?? helperSubmittedRecordPreview?.budgetAmount ?? 0;
  const helperPreviewTotalSpent = preview?.settlement.totalSpent ?? helperSubmittedRecordPreview?.totalSpent ?? 0;
  const helperPreviewBalance = preview?.settlement.balance ?? helperSubmittedRecordPreview?.balance ?? 0;
  const hasHelperPreviewCard = preview !== null || helperSubmittedRecordPreview !== null;
  const showHelperConfirmSettlementButton =
    preview !== null && helperSelectedMainBudgetId !== '';
  const helperReceivedVsExpensesBars = useMemo(
    () => buildHelperReceivedVsExpensesBars(helperMainBudgets),
    [helperMainBudgets]
  );
  const dailyTableRangeSameCalendarYear = useMemo(() => {
    if (!isIsoDate(helperSettlementStart) || !isIsoDate(helperSettlementEnd)) {
      return false;
    }
    return helperSettlementStart.slice(0, 4) === helperSettlementEnd.slice(0, 4);
  }, [helperSettlementEnd, helperSettlementStart]);

  useEffect(() => {
    if (!isEmployer || !employerTitleMenuOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!employerTitleMenuRef.current?.contains(event.target as Node)) {
        setEmployerTitleMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [employerTitleMenuOpen, isEmployer]);

  if (isEmployer) {
    return (
      <div className="min-h-full w-full min-w-0 overflow-x-hidden bg-[#f7f8fb] px-4 py-6 pb-24 dark:bg-gray-950">
        <div className="mx-auto min-w-0 max-w-lg">
          <AppPageTitleSection
            title={t('settlement.employerFinanceTitle')}
            endDecoration={
              <MoneyGivenCashWatermark
                inline
                opaque
                className="pointer-events-none absolute bottom-[5px] right-8 z-0 h-[6.6rem] w-[6.6rem] shrink-0"
              />
            }
          >
            <div className="relative z-10 shrink-0" ref={employerTitleMenuRef}>
              <button
                type="button"
                onClick={() => setEmployerTitleMenuOpen((prev) => !prev)}
                tabIndex={0}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200/80 bg-white text-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                aria-label={t('settlement.employerFinanceTitleMenuAria')}
                aria-expanded={employerTitleMenuOpen}
                aria-haspopup="menu"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}>
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="19" r="1" />
                  </g>
                </svg>
              </button>
              {employerTitleMenuOpen ? (
                <div
                  className="absolute right-0 top-9 z-50 w-44 overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={0}
                    className="flex w-full items-center gap-2 border-b border-gray-200/40 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
                    onClick={() => {
                      setEmployerTitleMenuOpen(false);
                      navigate('/settlements/pending-review');
                    }}
                  >
                    {t('settlement.employerFinanceMenuPendingReviewList')}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={0}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:text-gray-100 dark:hover:bg-gray-800"
                    onClick={() => {
                      setEmployerTitleMenuOpen(false);
                      navigate('/receipts');
                    }}
                  >
                    {t('settlement.employerFinanceMenuReceiptList')}
                  </button>
                </div>
              ) : null}
            </div>
          </AppPageTitleSection>
          <EmployerMoneyGivenSettlementSection />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full min-w-0 overflow-x-hidden bg-[#f7f8fb] px-4 py-6 pb-24 dark:bg-gray-950">
      <div className="mx-auto min-w-0 max-w-lg">
        <AppPageTitleSection
          title={t('settlement.helperHeroTitle')}
          subtitle={t('settlement.helperHeroSubtitle')}
          titleAccessory={<SettlementHelpPopover />}
          className="overflow-hidden"
          endDecoration={
            <MoneyGivenCashWatermark
              inline
              opaque
              className="pointer-events-none absolute top-[calc(10px-4rem)] right-0 z-0 h-[6.6rem] w-[6.6rem] shrink-0"
            />
          }
        />

      <main>
        {isHelper ? (
          <>
            {listError ? (
              <div
                className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200"
                role="alert"
              >
                {listError}
              </div>
            ) : null}
            <section
              id="helper-settlement-panel"
              className={`mb-6 overflow-hidden p-4 ${HELPER_EXPENSES_SOFT_CARD_CLASS}`}
              aria-labelledby="settlement-helper-budget-heading"
            >
              <h2
                id="settlement-helper-budget-heading"
                className="sr-only"
              >
                {t('settlement.formTitle')}
              </h2>
              <div>
                <label
                  htmlFor="helper-main-budget-select"
                  className="mb-2 block text-[15px] font-bold text-gray-900 dark:text-gray-100"
                >
                  {t('settlement.budgetLabel')}
                </label>
                <HelperMoneyReceivedPeriodSelect
                  id="helper-main-budget-select"
                  ariaLabel={t('settlement.budgetLabel')}
                  budgets={helperMainBudgets}
                  value={helperSelectedMainBudgetId}
                  onChange={commitHelperMainBudgetIdToUrl}
                />
              </div>
              {formError ? (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
                  {formError}
                </p>
              ) : null}
              {helperSubmittedNotice ? (
                <p className="mt-3 text-sm text-amber-700 dark:text-amber-300" role="status">
                  {helperSubmittedNotice}
                </p>
              ) : null}
              {previewLoading ? (
                <div
                  className="mt-4 flex justify-center py-8"
                  role="status"
                  aria-busy="true"
                  aria-label={t('settlement.previewing')}
                >
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
                </div>
              ) : hasHelperPreviewCard ? (
                <div className="mt-4 space-y-4">
                  <div className="grid min-w-0 grid-cols-2 gap-3 px-1 sm:gap-4">
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <span className="flex min-h-9 items-center gap-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                        <span>{t('settlement.helperStatPeriodBudget')}</span>
                        {preview ? (
                          <button
                            type="button"
                            onClick={() => setMoneyReceivedDetailModalOpen(true)}
                            className="inline-flex shrink-0 items-center justify-center bg-transparent p-0 text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-500 dark:hover:text-gray-400"
                            aria-label={t('settlement.moneyReceivedDetailOpen')}
                            aria-haspopup="dialog"
                            aria-expanded={moneyReceivedDetailModalOpen}
                          >
                            <HelperStatInfoIcon />
                          </button>
                        ) : null}
                      </span>
                      <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        {formatMoneyCompact(helperPreviewBudgetAmount)}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <span className="flex min-h-9 items-center gap-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                        <span>{t('settlement.helperStatTotalSpent')}</span>
                        {preview ? (
                          <button
                            type="button"
                            onClick={() => setDailyDetailModalOpen(true)}
                            className="inline-flex shrink-0 items-center justify-center bg-transparent p-0 text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-500 dark:hover:text-gray-400"
                            aria-label={t('settlement.dailyDetailOpen')}
                            aria-haspopup="dialog"
                            aria-expanded={dailyDetailModalOpen}
                          >
                            <HelperStatInfoIcon />
                          </button>
                        ) : null}
                      </span>
                      <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        {formatMoneyCompact(helperPreviewTotalSpent)}
                      </span>
                    </div>
                  </div>
                  <div className="flex min-w-0 items-center justify-between gap-2 border-t border-dashed border-gray-200 pt-4 dark:border-gray-600">
                    <span
                      className={`inline-flex min-h-10 min-w-0 items-center text-sm font-bold ${balanceClass(helperPreviewBalance)}`}
                    >
                      {balanceLabel(helperPreviewBalance)}
                    </span>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-right text-[clamp(1.75rem,7vw,2rem)] font-bold tracking-tight tabular-nums ${balanceClass(helperPreviewBalance)}`}
                      aria-label={`${balanceLabel(helperPreviewBalance)} ${formatMoney(Math.abs(helperPreviewBalance))}`}
                    >
                      {helperPreviewBalance < 0 ? (
                        <svg className="h-[0.9em] w-[0.9em] shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM6.75 9.25a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg className="h-[0.9em] w-[0.9em] shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      <span>{formatMoney(Math.abs(helperPreviewBalance))}</span>
                    </span>
                  </div>
                  {showHelperConfirmSettlementButton ? (
                    <Link
                      to={{ pathname: '/settlements/confirm', search: helperConfirmSearch }}
                      className="flex w-full min-h-11 items-center justify-center rounded-xl border-2 border-primary-600 bg-white px-4 py-2.5 text-sm font-bold text-primary-700 shadow-[0_0_12px_rgba(11,110,79,0.1)] transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-primary-500 dark:bg-gray-900 dark:text-primary-300 dark:hover:bg-primary-950/40"
                      aria-label={t('settlement.helperSettleButton')}
                    >
                      {t('settlement.helperSettleButton')}
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </section>

            <div className="mb-6">
              <MoneyGivenVsExpensesChart
                bars={helperReceivedVsExpensesBars}
                loading={helperMainBudgetsLoading}
                emptyLabel={t('budget.empty')}
                title={t('settlement.helperReceivedVsExpensesTitle')}
                subtitle={t('settlement.helperReceivedVsExpensesSubtitle')}
                givenLabel={t('settlement.helperStatPeriodBudget')}
                expensesLabel={t('settlement.helperStatTotalSpent')}
                pendingExpensesLabel={t('settlement.helperStatTotalSpent')}
              />
            </div>

            <section className="mb-8" aria-labelledby="helper-quick-actions-heading">
              <h2
                id="helper-quick-actions-heading"
                className="mb-3 text-base font-bold text-gray-900 dark:text-gray-100"
              >
                {t('settlement.helperQuickActions')}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/receipts/analyze"
                  className={HELPER_QUICK_ACTION_CARD_CLASS}
                  aria-label={t('helperHome.actionScanReceipt')}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-950/50">
                    <svg className="h-5 w-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  </span>
                  <span className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('helperHome.actionScanReceipt')}
                  </span>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('helperHome.actionScanReceiptSub')}
                  </span>
                </Link>

                <Link
                  to="/receipts"
                  className={HELPER_QUICK_ACTION_CARD_CLASS}
                  aria-label={t('helperHome.actionReceiptList')}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-950/40">
                    <svg className="h-5 w-5 text-sky-600 dark:text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  </span>
                  <span className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('helperHome.actionReceiptList')}
                  </span>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('helperHome.actionReceiptListSub')}
                  </span>
                </Link>

                <Link
                  to={{ pathname: '/settlements/confirm', search: helperConfirmSearch }}
                  className={HELPER_QUICK_ACTION_CARD_CLASS}
                  aria-label={t('settlement.helperActionLockTitle')}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 dark:bg-orange-950/40">
                    <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  </span>
                  <span className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('settlement.helperActionLockTitle')}
                  </span>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('settlement.helperActionLockMeta')}
                  </span>
                </Link>

                <Link
                  to="/settlements/history"
                  className={HELPER_QUICK_ACTION_CARD_CLASS}
                  aria-label={t('settlement.helperActionHistoryTitle')}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-950/40">
                    <svg className="h-5 w-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  </span>
                  <span className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('settlement.helperActionHistoryTitle')}
                  </span>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('settlement.helperActionHistoryMeta')}
                  </span>
                </Link>
              </div>
            </section>
          </>
        ) : null}
      </main>

      {isHelper ? (
        <PagePrimaryFab to="/receipts/analyze" ariaLabel={t('settlement.helperActionScanTitle')}>
          <span aria-hidden className="text-lg leading-none">
            +
          </span>
          {t('settlement.helperActionScanTitle')}
        </PagePrimaryFab>
      ) : null}

      {dailyDetailModalOpen && preview ? (
        <div
          className={`${MODAL_BACKDROP_BOTTOM_CLASS} z-50 ${MODAL_BACKDROP_SCRIM_CLASS}`}
          role="presentation"
          onClick={() => setDailyDetailModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settlement-daily-detail-title"
            className={`flex w-full max-w-md flex-col rounded-xl bg-white p-5 shadow-lg dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3
                  id="settlement-daily-detail-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t('settlement.breakdownModalTitle')}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {formatSettlementPeriodDisplay(preview.settlement.startDate, preview.settlement.endDate, t)}
                </p>
              </div>
              <ModalCircleCloseButton
                onClick={() => setDailyDetailModalOpen(false)}
                ariaLabel={t('common.closeViewer')}
              />
            </div>
            <div className="mt-4 overflow-y-auto pr-1">
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                      {t('settlement.dailyDetailDate')}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-200">
                      {t('settlement.dailyDetailReceiptCount')}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-200">
                      {t('settlement.dailyDetailAmount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {preview.dailyBreakdown.map((row) => (
                    <tr key={row.date}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        {formatIsoDateForDailyBreakdownTable(row.date, dailyTableRangeSameCalendarYear)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">
                        {row.receiptCount}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatMoneyCompact(row.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {moneyReceivedDetailModalOpen && preview ? (
        <MoneyReceivedDetailModal
          open
          onClose={() => setMoneyReceivedDetailModalOpen(false)}
          settlementStart={preview.settlement.startDate}
          settlementEnd={preview.settlement.endDate}
          rows={preview.moneyReceivedDetails}
          t={t}
          titleKey="settlement.moneyReceivedDetailTitle"
          titleId="settlement-money-received-detail-title"
        />
      ) : null}
      </div>
    </div>
  );
};
